import { ensureDefined } from '@avantia/client-and-server-utilities';
import { BaseSyntheticEvent } from 'react';
import { triggerModalActionEvent } from '../../../engineClient/actions/eventActions';
import { createStandardFormEvent } from '../../../engineClient/components/formControls/inputControl';
import { HpImageButtonListControlProps } from '../../../engineClient/components/formControls/standard';
import { ImageWidget } from '../../../engineClient/components/widgets';
import { showInformationModal } from '../../../engineClient/components/widgets/modalWidget';
import {
  HpFormGroupClassTypes,
  StandardFormGroupWidget
} from '../../../engineClient/components/widgets/standardFormGroupWidget';
import {
  HpClientQuestion,
  HpClientQuestionData,
  HpClientQuestionLookupItemMap,
  HpClientStandardInputTextAndStyle,
  HpViewportState
} from '../../../engineClient/types';
import type { HeadingAndBody, HpClaimsCommonCustomData, PolicyModalCopyText } from '../../../tools/utilities';
import { PolicyMap } from '../../../types/policyTypes';
import { HpClaimsQuestionSetPageProps } from '../../questionSetForm/questionSetPageTypes';
import { PolicyInformationWidget } from '../../widgets/policyInformationWidget';
import { createPolicyOptionText } from './selectPolicyCustomControl';

export type CheckYourCoverCustomControlProps = HpImageButtonListControlProps;
interface CheckYourCoverCards {
  checkYourCover: HeadingAndBody;
  logAnIncident: HeadingAndBody;
  submitAClaim: HeadingAndBody;
}
export interface CheckYourCoverCustomData {
  cardsCopyText: CheckYourCoverCards;
}

const docIcon = 'document-icon.svg';

export function CheckYourCoverCustomControl(props: CheckYourCoverCustomControlProps): JSX.Element | null {
  const { question, id, pageProps, viewport, formatType } = props;
  const { policies, commonCustomData } = pageProps as HpClaimsQuestionSetPageProps;
  const { copyText } = commonCustomData as unknown as HpClaimsCommonCustomData;
  const { policyCoverModal } = copyText;
  const data = question.data as HpClientQuestionData;
  const { customData, textAndStyle: dataTextAndStyle } = data as HpClientQuestionData;
  const textAndStyle = dataTextAndStyle[formatType] as HpClientStandardInputTextAndStyle;
  const checkYourCoverCustomData = ensureDefined(customData, 'customData') as unknown as CheckYourCoverCustomData;
  const { cardsCopyText } = checkYourCoverCustomData;
  ensureDefined(cardsCopyText, 'cardCopyText');

  const checkYourCoverCardsKeys = Object.keys(cardsCopyText);
  const [youCoverCard, logAnIncidentCard, submitAClaimCard] = checkYourCoverCardsKeys.map((key, index) => {
    const cardCopyText = cardsCopyText[key] as HeadingAndBody;
    const { heading, body } = cardCopyText;
    if (!heading || !body) {
      throw new Error('CheckYourCoverCustomControl customData is incomplete.');
    }

    return createCheckYourCoverCard({ number: `${index + 1}`, heading, body });
  });

  const lookup: HpClientQuestionLookupItemMap = {};
  let updatedData: HpClientQuestionData | null = null;
  for (const policyReference in policies) {
    const policyInstance = policies[policyReference];
    const coverTypeText = policyInstance && policyInstance.coverType;
    lookup[policyReference] = { text: `${coverTypeText}` };
    updatedData = { ...data, lookup } as HpClientQuestionData;
  }

  const updatedQuestion = { ...question, data: updatedData || (data as HpClientQuestionData) };

  const onChangeHandler = createShowPolicyDetailsModalEvent({
    question: updatedQuestion,
    id,
    policies,
    policyModalCopyText: policyCoverModal,
    viewport
  });

  const changeEvent = createStandardFormEvent(onChangeHandler, updatedQuestion);

  const createPolicyHeader = (coverType: string, policyReference: string) => {
    return (
      <>
        <a onClick={changeEvent} id={policyReference}>
          <ImageWidget selected={false} image={docIcon} altText="document icon" />
          {coverType} Insurance
        </a>
      </>
    );
  };

  const policyWidgetList: JSX.Element[] = [];
  for (const policyRef in policies) {
    policyWidgetList.push(createPolicyOptionText(policyRef, policies, createPolicyHeader));
  }

  return (
    <div className="container">
      {youCoverCard}
      <div>
        <StandardFormGroupWidget
          textAndStyle={textAndStyle}
          id={id}
          question={question}
          questionText={''}
          control={
            <div className="btn-group-vertical check-your-cover-policies" role="group">
              {policyWidgetList.map((policyWidget) => policyWidget)}
            </div>
          }
          controlType={`image-vertical-button-list` as HpFormGroupClassTypes}
          dataType={'string'}
          answerSource={'unanswered'}
          error={undefined}
          subtext={undefined}
        />
      </div>
      {logAnIncidentCard}
      {submitAClaimCard}
    </div>
  );
}

interface CreateCheckYourCoverCardProps {
  body: string;
  heading: string;
  number: string;
}

function createCheckYourCoverCard({ body, heading, number }: CreateCheckYourCoverCardProps) {
  return (
    <div className="check-your-cover-card">
      <div>
        <h2>
          <div className="hp-badge">{number}</div>
          {heading}
        </h2>
      </div>
      <div>{body}</div>
    </div>
  );
}

interface CreateShowPolicyDetailsModalEventProps {
  id: string;
  policies: PolicyMap;
  policyModalCopyText: PolicyModalCopyText;
  question: HpClientQuestion;
  viewport: HpViewportState;
}

export function createShowPolicyDetailsModalEvent({
  question,
  id,
  policies,
  policyModalCopyText,
  viewport
}: CreateShowPolicyDetailsModalEventProps): (event: BaseSyntheticEvent) => void {
  return (event) => {
    event.stopPropagation();
    const policyReference = event.currentTarget.id;
    const policy = policies[policyReference];
    triggerModalActionEvent({ question, getEvent: () => event, buttonType: 'show' });
    return showInformationModal({
      question,
      id,
      modalClass: 'hp-policy-modal-dialog',
      modalHeaderClass: 'hp-heading-control',
      modalCopyText: {
        body: <PolicyInformationWidget policy={policy} policyModalCopyText={policyModalCopyText} viewport={viewport} />,
        cancelButtonText: null,
        confirmButtonText: null,
        confirmButtonStyle: 'secondary'
      }
    });
  };
}
